import { useState, useEffect } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { supabase } from "@/integrations/supabase/client";
import { Check } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

interface ChatBackgroundSelectorProps {
  organizationId: string;
  currentBackground: string;
  onBackgroundChange: (background: string) => Promise<void>;
}

interface BackgroundOption {
  id: string;
  name: string;
  thumbnail: string;
  type: "default" | "image";
}

const ChatBackgroundSelector: React.FC<ChatBackgroundSelectorProps> = ({
  organizationId,
  currentBackground,
  onBackgroundChange,
}) => {
  const [backgrounds, setBackgrounds] = useState<BackgroundOption[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedBackground, setSelectedBackground] =
    useState(currentBackground);
  const [isChanging, setIsChanging] = useState(false);
  const { toast } = useToast();

  // Default linear gradient background option
  const defaultBackgrounds: BackgroundOption[] = [
    {
      id: "default",
      name: "Default - Linear Background",
      thumbnail: "/default-bg-thumbnail.svg", // We'll create this
      type: "default",
    },
  ];

  useEffect(() => {
    const fetchBackgrounds = async () => {
      setIsLoading(true);
      try {
        // Fetch background images from Supabase storage
        const { data: files, error } = await supabase.storage
          .from("image_library")
          .list("chat_backgrounds", {
            sortBy: { column: "name", order: "asc" },
          });

        console.log("Background files response:", { files, error });

        if (error) {
          throw error;
        }

        if (files && files.length > 0) {
          // Convert storage files to background options
          const imageBackgrounds = files
            .filter(
              (file) => !file.name.startsWith(".") && !file.name.endsWith("/"),
            )
            .map((file) => {
              const fileName = file.name;
              const displayName = fileName
                .replace(/\.(jpg|jpeg|png|webp|svg)$/i, "")
                .split("_")
                .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                .join(" ");

              // Create a public URL for the image
              const publicUrl = supabase.storage
                .from("image_library")
                .getPublicUrl(`chat_backgrounds/${fileName}`).data.publicUrl;

              return {
                id: fileName,
                name: displayName,
                thumbnail: publicUrl,
                type: "image" as const,
              };
            });

          // Combine default backgrounds with image backgrounds
          setBackgrounds([...defaultBackgrounds, ...imageBackgrounds]);
        } else {
          setBackgrounds(defaultBackgrounds);
        }
      } catch (error) {
        console.error("Error fetching backgrounds:", error);
        toast({
          variant: "destructive",
          title: "Failed to load backgrounds",
          description:
            "There was an error loading the background options. Please try again later.",
        });
        setBackgrounds(defaultBackgrounds);
      } finally {
        setIsLoading(false);
      }
    };

    fetchBackgrounds();
  }, [organizationId]);

  const handleSelectBackground = async (background: BackgroundOption) => {
    if (background.id === selectedBackground || isChanging) return;

    setIsChanging(true);
    try {
      await onBackgroundChange(background.id);
      setSelectedBackground(background.id);
      toast({
        title: "Background updated",
        description: "Assistant background has been updated successfully.",
      });
    } catch (error) {
      console.error("Error updating background:", error);
      toast({
        variant: "destructive",
        title: "Failed to update background",
        description:
          "There was an error updating the assistant background. Please try again.",
      });
    } finally {
      setIsChanging(false);
    }
  };

  if (isLoading) {
    return (
      <div className="p-4 text-center">
        <p className="text-sm text-muted-foreground">Loading backgrounds...</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground pb-4">
        Select a background for your AI assistant. This will be applied to both
        the assistant splash page and assistant UI.
      </p>

      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
        {backgrounds.map((background) => (
          <Card
            key={background.id}
            className={`relative overflow-hidden cursor-pointer transition-all border-2 hover:border-primary ${
              selectedBackground === background.id
                ? "border-primary ring-2 ring-primary/20"
                : "border-transparent"
            }`}
            onClick={() => handleSelectBackground(background)}
          >
            <div className="relative aspect-video overflow-hidden">
              {background.type === "default" ? (
                <div
                  className="w-full h-full"
                  style={{
                    background:
                      "linear-gradient(to bottom, #3B82F615 0%, #ffffff 100%)",
                  }}
                />
              ) : (
                <img
                  src={background.thumbnail}
                  alt={background.name}
                  className="w-full h-full object-cover"
                />
              )}
            </div>
            <CardContent className="pt-2 pb-2 px-2">
              <p className="text-xs text-center font-medium truncate">
                {background.name}
              </p>
            </CardContent>

            {/* Selected checkmark */}
            {selectedBackground === background.id && (
              <div className="absolute top-2 right-2 bg-primary text-white p-1 rounded-full">
                <Check className="h-3 w-3" />
              </div>
            )}
          </Card>
        ))}
      </div>
    </div>
  );
};

export default ChatBackgroundSelector;
